import {
  ChatMessage,
  ChatRole,
  isChatTextContent,
  ClientViewingContext,
} from "@/shared/domain/chat/ChatRecord";

import { isEmptyContext } from "../contextUtils";

const CTX_REGEX = /^<ctx>The above was sent with context: (.+)<\/ctx>$/;

/**
 * Attempts to parse a ClientViewingContext from a ROBOTO-role message
 * that contains a `<ctx>...</ctx>` text block.
 */
export function parseContextFromMessage(
  message: ChatMessage,
): ClientViewingContext | null {
  if (message.role !== ChatRole.ROBOTO) {
    return null;
  }

  for (const content of message.content) {
    if (!isChatTextContent(content)) {
      continue;
    }

    const match = content.text.match(CTX_REGEX);
    if (match) {
      try {
        return JSON.parse(match[1]) as ClientViewingContext;
      } catch {
        return null;
      }
    }
  }

  return null;
}

// ---------------------------------------------------------------------------
// Incremental scan infrastructure
// ---------------------------------------------------------------------------

export interface ScanState {
  previousContext: ClientViewingContext | null;
}

export interface ScanResult {
  shifts: Map<number, ClientViewingContext>;
  endState: ScanState;
}

/**
 * Scans messages starting at `fromIndex` and returns context shifts found,
 * plus the end state so callers can resume later without re-processing.
 *
 * Empty contexts are only emitted when transitioning from non-empty → empty
 * (user navigated away). Initial empty contexts (when previousContext is null
 * or also empty) are suppressed.
 */
export function scanForShifts(
  messages: ChatMessage[],
  fromIndex: number,
  initialState: ScanState,
): ScanResult {
  const shifts = new Map<number, ClientViewingContext>();
  let previousContext = initialState.previousContext;

  for (let i = fromIndex; i < messages.length; i++) {
    const ctx = parseContextFromMessage(messages[i]);
    if (ctx === null) {
      continue;
    }

    // Skip empty contexts when there is no previous non-empty context
    // (i.e. the conversation starts with empty context — nothing to show).
    // But DO show empty contexts when transitioning from non-empty → empty,
    // since the user navigated away from something.
    if (
      isEmptyContext(ctx) &&
      (previousContext === null || isEmptyContext(previousContext))
    ) {
      previousContext = ctx;
      continue;
    }

    if (!contextsEqual(previousContext, ctx)) {
      // Walk backwards from the ctx message to find the preceding USER message
      const userIndex = findPrecedingUserMessage(messages, i);
      if (userIndex !== null) {
        shifts.set(userIndex, ctx);
      }
    }
    previousContext = ctx;
  }

  return { shifts, endState: { previousContext } };
}

// ---------------------------------------------------------------------------
// Public API — convenience wrapper for full-scan use cases and tests
// ---------------------------------------------------------------------------

/**
 * Scans a list of messages and returns context shifts — positions where
 * the embedded context changed compared to the previous context.
 *
 * Returns a Map keyed by the index of the USER message that precedes each
 * context message. This allows rendering the indicator above the user's
 * message rather than below it.
 *
 * Empty contexts are only shown when the user transitions from a non-empty
 * context to an empty one (i.e. navigated away). An initial empty context
 * at the start of a conversation is suppressed.
 */
export function detectContextShifts(
  messages: ChatMessage[],
): Map<number, ClientViewingContext> {
  return scanForShifts(messages, 0, { previousContext: null }).shifts;
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * Walks backwards from `startIndex` to find the first USER message.
 */
function findPrecedingUserMessage(
  messages: ChatMessage[],
  startIndex: number,
): number | null {
  for (let j = startIndex - 1; j >= 0; j--) {
    if (messages[j].role === ChatRole.USER) {
      return j;
    }
  }
  return null;
}

function contextsEqual(
  a: ClientViewingContext | null,
  b: ClientViewingContext,
): boolean {
  if (a === null) {
    return false;
  }

  // Compare dataset_ids
  if (a.dataset_ids.length !== b.dataset_ids.length) {
    return false;
  }
  for (let i = 0; i < a.dataset_ids.length; i++) {
    if (a.dataset_ids[i] !== b.dataset_ids[i]) {
      return false;
    }
  }

  // Compare file_ids
  if (a.file_ids.length !== b.file_ids.length) {
    return false;
  }
  for (let i = 0; i < a.file_ids.length; i++) {
    if (a.file_ids[i] !== b.file_ids[i]) {
      return false;
    }
  }

  // Compare visualizer_state presence
  const aHasViz =
    a.visualizer_state !== undefined && a.visualizer_state !== null;
  const bHasViz =
    b.visualizer_state !== undefined && b.visualizer_state !== null;
  if (aHasViz !== bHasViz) {
    return false;
  }
  if (aHasViz && bHasViz) {
    if (
      JSON.stringify(a.visualizer_state) !== JSON.stringify(b.visualizer_state)
    ) {
      return false;
    }
  }

  return true;
}
